import { useEffect, useRef, type ReactNode } from 'react';

interface DraggableMarqueeProps {
  children: ReactNode;
  /** Скорость автоскролла в px/сек. По умолчанию 60. */
  speed?: number;
  /** Минимальное движение в пикселях, после которого жест считается drag-ом (а не tap-ом). */
  dragThreshold?: number;
}

/**
 * Бесшовная карусель с drag-перетаскиванием.
 *
 * - дети рендерятся дважды снаружи (для бесшовного цикла);
 *   позиция оборачивается по модулю половины ширины ленты.
 * - автопрокрутка через requestAnimationFrame.
 * - hover ставит на паузу; drag тоже ставит на паузу.
 * - если суммарное смещение мыши превысило threshold - последующий click
 *   глушится в capture-фазе, чтобы flip-карточки не «случайно» переворачивались.
 */
export function DraggableMarquee({
  children,
  speed = 60,
  dragThreshold = 6,
}: DraggableMarqueeProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);

  const offsetRef = useRef(0);
  const halfWidthRef = useRef(0);

  const isHoveringRef = useRef(false);
  const isPointerDownRef = useRef(false);
  const isDraggingRef = useRef(false);
  const wasDraggedRef = useRef(false);
  const dragStartRef = useRef({ x: 0, offset: 0 });

  // Применяет текущий offset к транформации ленты.
  const apply = () => {
    if (trackRef.current) {
      trackRef.current.style.transform = `translate3d(${-offsetRef.current}px, 0, 0)`;
    }
  };

  // Пересчёт ширины «половинки» (контент удвоен снаружи).
  useEffect(() => {
    const update = () => {
      if (trackRef.current) halfWidthRef.current = trackRef.current.scrollWidth / 2;
    };
    update();
    const ro = new ResizeObserver(update);
    if (trackRef.current) ro.observe(trackRef.current);
    return () => ro.disconnect();
  }, []);

  // Главный цикл анимации.
  useEffect(() => {
    let raf = 0;
    let last = performance.now();

    const tick = (now: number) => {
      const dt = (now - last) / 1000;
      last = now;

      const reduce =
        typeof window !== 'undefined' &&
        window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

      if (
        !isHoveringRef.current &&
        !isPointerDownRef.current &&
        !reduce &&
        halfWidthRef.current > 0
      ) {
        let v = offsetRef.current + speed * dt;
        const w = halfWidthRef.current;
        v = ((v % w) + w) % w;
        offsetRef.current = v;
        apply();
      }

      raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [speed]);

  const onPointerDown = (e: React.PointerEvent) => {
    isPointerDownRef.current = true;
    isDraggingRef.current = false;
    wasDraggedRef.current = false;
    dragStartRef.current = { x: e.clientX, offset: offsetRef.current };
    containerRef.current?.setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!isPointerDownRef.current) return;
    const dx = e.clientX - dragStartRef.current.x;
    if (!isDraggingRef.current && Math.abs(dx) >= dragThreshold) {
      isDraggingRef.current = true;
      wasDraggedRef.current = true;
    }
    if (!isDraggingRef.current) return;

    let v = dragStartRef.current.offset - dx;
    const w = halfWidthRef.current;
    if (w > 0) v = ((v % w) + w) % w;
    offsetRef.current = v;
    apply();
  };

  const endDrag = (e: React.PointerEvent) => {
    isPointerDownRef.current = false;
    isDraggingRef.current = false;
    try {
      containerRef.current?.releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
  };

  // Глушим клик, если перед ним был драг - иначе flip-карточка перевернётся.
  const onClickCapture = (e: React.MouseEvent) => {
    if (wasDraggedRef.current) {
      e.stopPropagation();
      e.preventDefault();
      wasDraggedRef.current = false;
    }
  };

  return (
    <div
      ref={containerRef}
      className="marquee-mask overflow-hidden cursor-grab active:cursor-grabbing select-none"
      onMouseEnter={() => {
        isHoveringRef.current = true;
      }}
      onMouseLeave={() => {
        isHoveringRef.current = false;
      }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onClickCapture={onClickCapture}
    >
      <div ref={trackRef} className="marquee-track flex gap-5 w-max">
        {children}
        {children}
      </div>
    </div>
  );
}
